import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Row, Col, Dropdown, Form, Table } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';

import {
  requestInsert,
  requestUpdate,
  requestDelete,
  requestInform as request,
  requestDownload,
  requestReject
} from 'api/board/rawMaterial';
import cloudUpload from 'assets/img/icons/cloud-upload.svg';
import { requestList as requestCode } from 'api/standard/code';
import CardDropdown from 'components/common/CardDropdown';
import Divider from 'components/common/Divider';
import Flex from 'components/common/Flex';
import Content from 'components/common-sfims/Content';
import Select from 'components/common-sfims/form/Select';
import List from 'components/common-sfims/List';
import Comment from 'components/pages/board/rawMaterial/Comment';
import Create from 'components/pages/board/rawMaterial/Create';
import { array2ValueAndText, number2format } from 'function/convert';
import { onInputChange } from 'function/event';
import { downloadFile } from 'function/file';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import { getSize } from 'helpers/utils';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import { RETURN_YN } from 'types/type';
import valueFrom from 'utils/valueFrom';
import pluck from 'utils/array/pluck';
import formatNumber from 'utils/format/number';
import format from 'utils/string/format';

/**
 * 협업 게시판 > 신규 원료 코드 신청 - 신청/수정/조회
 */
const Inform = () => {
  /* variable */
  const { adminYn } = getLoginInform();

  /* router */
  const history = useHistory();
  const { pathname, state } = useLocation();
  const { seq } = useParams();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* dropzone */
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 3,
    maxSize: 50000000,
    onDrop: (file, [{ errors: [error = {}] = [] } = {}]) => {
      try {
        switch (error.code) {
          case 'too-many-files':
            throw format(getText('tooManyFilesMessage'), { max: 3 });

          case 'file-too-large':
            throw 'fileTooLarge';
        }

        if (file.length + inform.newFileList?.length > 3) {
          throw format(getText('tooManyFilesMessage'), { max: 3 });
        }

        if (
          file.some(({ name }) =>
            ['exe', 'bat', 'msi'].includes(name.substr(-3).toLowerCase())
          )
        ) {
          throw 'notAcceptFileExe';
        }

        setRawMaterialData({ file });
      } catch (message) {
        setConfirmModal({
          isOpen: true,
          message
        });
      }
    }
  });

  /* state */
  // 신규 원료 코드 신청 정보
  const [inform, setInform] = useObjReducer({});
  // 코드 만들기 여부
  const [isCreate, setIsCreate] = useState(false);
  // 삭제 비활성화 여부
  const [isDisableDelete, setIsDisableDelete] = useState(false);
  // 반려 비활성화 여부
  const [isDisableReject, setIsDisableReject] = useState(false);
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // 코드 만들기 모달 표시 여부
  const [isShowCreate, setIsShowCreate] = useState(false);
  // 하위 분류 목록
  const [mtrilGrpCds, setMtrilGrpCds] = useState([]);
  // 신규 원료 코드 신청 (수정) 요청 데이터
  const [rawMaterialData, setRawMaterialData] = useObjReducer({
    rwmtrNm: '',
    usePurps: '',
    mtrilGrpCd: '',
    moisture: new Array(3).fill(),
    cp: new Array(3).fill(),
    cFat: new Array(3).fill(),
    cFib: new Array(3).fill(),
    ash: new Array(3).fill(),
    ca: new Array(3).fill(),
    p: new Array(3).fill(),
    starch: new Array(3).fill(),
    note: ['Supplier', 'NIR', 'Wet']
  });

  /* watch */
  useEffect(() => {
    // 수정/조회 화면인 경우 신규 원료 코드 신청 정보 조회 요청
    if (seq) {
      requestInform();
    }

    // 하위 분류 목록 조회 요청
    requestMtrilGrpCd();
  }, []);

  /* method */
  // 코드 만들기
  const create = data => {
    if (pathname.includes('setting')) {
      setIsCreate(true);
      setRawMaterialData(data);

      return;
    }

    showList();
  };
  // 첨부 파일 다운로드 요청
  const requestFileDownload = async seq => {
    try {
      const { data, name } = await requestDownload(seq);

      downloadFile(data, name);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    }
  };
  // 신규 원료 코드 신청 정보 조회 요청
  const requestInform = async () => {
    try {
      const { data = {}, code, message } = await request(seq);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      // 수정 화면인 경우
      if (pathname.includes('setting') && seq) {
        // 권한이 없는 경우
        if (adminYn !== RETURN_YN.Y && data.userFlag !== RETURN_YN.Y) {
          throw { code: RESPONSE_CODE.Forbidden };
        }

        // 완료 상태인 경우
        if (data.sttus === 1) {
          throw { code: 'sttusMessage' };
        }
      }

      setInform(data);
      setRawMaterialData({
        rwmtrNm: data.rwmtrNm ?? '',
        usePurps: data.usePurps ?? '',
        mtrilGrpCd: data.mtrilGrpCd ?? '',
        moisture: pluck(data.newNutritionList, 'moisture'),
        cp: pluck(data.newNutritionList, 'cp'),
        cFat: pluck(data.newNutritionList, 'cFat'),
        cFib: pluck(data.newNutritionList, 'cFib'),
        ash: pluck(data.newNutritionList, 'ash'),
        ca: pluck(data.newNutritionList, 'ca'),
        p: pluck(data.newNutritionList, 'p'),
        starch: pluck(data.newNutritionList, 'starch'),
        note: valueFrom(pluck(data.newNutritionList, 'note'), [
          'Supplier',
          'NIR',
          'Wet'
        ]),
        fileSeq: pluck(data.newFileList, 'seq')
      });
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });

      // 목록 화면 표시
      showList();
    }
  };
  // 하위 분류 목록 조회 요청
  const requestMtrilGrpCd = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'rwmtr_subclass' });

    setMtrilGrpCds(list);
  };
  // 영양가 설정
  const setNutrition = (key, index, nutrition) => {
    nutrition = number2format(nutrition, ',0.##', 99.99);

    if (nutrition === false) {
      return;
    }

    const nutritions = [...rawMaterialData[key]];

    nutritions[index] = nutrition;

    setRawMaterialData({ [key]: nutritions });
  };
  // 목록 화면 표시
  const showList = () => {
    history.push(ROUTER_PATH.board.rawMaterial.list, state);
  };

  /* event */
  // 삭제 클릭 시
  const onDeleteClick = () => {
    setConfirmModal({
      isOpen: true,
      title: 'delete',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      async onConfirm() {
        setIsDisableDelete(true);

        try {
          const { code, message } = await requestDelete(seq);

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 목록 화면 표시
          showList();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
          setIsDisableDelete(false);
        }
      }
    });
  };
  // 첨부 파일 삭제 클릭 시
  const onDeleteFileClick = (index, isReal = false) => {
    setRawMaterialData({
      file: (rawMaterialData.file ?? []).filter((value, idx) => idx !== index)
    });

    if (isReal) {
      setInform({
        newFileList: inform.newFileList.filter(({ seq } = {}) => seq !== index)
      });
      setRawMaterialData({
        fileSeq: rawMaterialData.fileSeq.filter(value => value !== index)
      });
    }
  };
  // 반려 클릭 시
  const onRejectClick = () => {
    setConfirmModal({
      isOpen: true,
      title: 'reject',
      message: 'rejectMessage',
      isCancelBtn: true,
      btnNm: 'reject',
      async onConfirm() {
        setIsDisableReject(true);

        try {
          const { code, message } = await requestReject(seq);

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 목록 화면 표시
          showList();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
          setIsDisableReject(false);
        }
      }
    });
  };
  // 설정
  const onSubmit = event => {
    event.preventDefault();

    setConfirmModal({
      isOpen: true,
      title: seq ? 'updateRawMaterial' : 'insertRawMaterial',
      message: seq ? 'updateRawMaterialMessage' : 'insertRawMaterialMessage',
      isCancelBtn: true,
      async onConfirm() {
        setIsDisableSetting(true);

        try {
          // 필수 항목 입력 유효성 확인
          if (
            !isValidRequired(rawMaterialData, [
              'moisture',
              'cp',
              'cFat',
              'cFib',
              'ash',
              'ca',
              'p',
              'starch',
              'note',
              'file',
              'fileSeq'
            ])
          ) {
            throw {
              code: RESPONSE_CODE[seq ? 'BadRequestUpdate' : 'BadRequestInsert']
            };
          }

          if (seq) {
            const { code, message } = await requestUpdate(seq, rawMaterialData);

            if (code !== RESPONSE_CODE.Success) {
              throw {
                code,
                error: message
              };
            }
          } else {
            if (adminYn === RETURN_YN.Y && !isCreate) {
              throw { code: 'createRawMaterialMessage' };
            }

            const { code, message } = await requestInsert(rawMaterialData);

            if (code !== RESPONSE_CODE.Success) {
              throw {
                code,
                error: message
              };
            }
          }

          // 목록 화면 표시
          showList();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
          setIsDisableSetting(false);
        }
      }
    });
  };

  /* view */
  return (
    <>
      <Form onSubmit={onSubmit}>
        <Content className={{ body: 'bg-light' }}>
          {pathname.includes('setting') && (
            <Fragment key="title">
              {getText(seq ? 'update' : 'insert')}
            </Fragment>
          )}
          <Fragment key="body">
            <Row className="mb-3">
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>
                    {pathname.includes('setting') && '* '}
                    {getText('rwmtrNm')}
                  </Form.Label>
                  <Form.Control
                    readOnly={!pathname.includes('setting')}
                    size="sm"
                    type="text"
                    value={rawMaterialData.rwmtrNm}
                    autoFocus={pathname.includes('setting')}
                    maxLength={30}
                    name="rwmtrNm"
                    required
                    onChange={event => {
                      if (pathname.includes('setting')) {
                        onInputChange(event, setRawMaterialData);
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={8}>
                <Form.Group>
                  <Form.Label>
                    {pathname.includes('setting') && '* '}
                    {getText('usePurps')}
                  </Form.Label>
                  <Form.Control
                    readOnly={!pathname.includes('setting')}
                    size="sm"
                    type="text"
                    value={rawMaterialData.usePurps}
                    maxLength={300}
                    name="usePurps"
                    required
                    onChange={event => {
                      if (pathname.includes('setting')) {
                        onInputChange(event, setRawMaterialData);
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>
                    {pathname.includes('setting') && '* '}
                    {getText('rawMaterialCtgryNmKr')}
                  </Form.Label>
                  <Table borderless className="fs--1">
                    <tbody>
                      <tr>
                        <th className="align-middle">
                          {getText('rawMaterialCtgryCd')}
                        </th>
                        <td>
                          <Form.Control
                            readOnly
                            size="sm"
                            type="text"
                            value={rawMaterialData.mtrilGrpCd}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th className="align-middle">Item</th>
                        <td>
                          {pathname.includes('setting') ? (
                            <Select
                              datas={array2ValueAndText(
                                mtrilGrpCds,
                                'codeCd',
                                'codeNmKr'
                              )}
                              placeholder="Item"
                              value={rawMaterialData.mtrilGrpCd}
                              name="mtrilGrpCd"
                              required
                              onChange={event => {
                                onInputChange(event, setRawMaterialData);
                              }}
                            />
                          ) : (
                            <>
                              {/* View */}
                              <Form.Control
                                readOnly
                                size="sm"
                                type="text"
                                value={inform.mtrilGrpNm}
                              />
                            </>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Form.Group>
              </Col>
              <Col sm={8}>
                <Form.Label>{getText('nutrition')}</Form.Label>
                <List
                  className={{ tbody: 'bg-white' }}
                  columns={[
                    'Item',
                    'Moist.',
                    'CP',
                    'CFAT',
                    'CFIB',
                    'ASH',
                    'Ca',
                    'P',
                    'Starch',
                    'Note'
                  ]}
                >
                  {rawMaterialData.note.map((value, index) => (
                    <tr key={`nutritions.${value}`}>
                      {index === 0 && (
                        <td className="align-middle" rowSpan={3}>
                          %
                        </td>
                      )}
                      <td className="px-2">
                        <Form.Control
                          readOnly={!pathname.includes('setting')}
                          size="sm"
                          type="text"
                          value={
                            (!pathname.includes('setting')
                              ? formatNumber(
                                  inform.newNutritionList?.[index]?.moisture,
                                  ',0.00'
                                )
                              : rawMaterialData.moisture[index]) ?? ''
                          }
                          onChange={({ target: { value } }) => {
                            setNutrition('moisture', index, value);
                          }}
                        />
                      </td>
                      <td className="px-2">
                        <Form.Control
                          readOnly={!pathname.includes('setting')}
                          size="sm"
                          type="text"
                          value={
                            (!pathname.includes('setting')
                              ? formatNumber(
                                  inform.newNutritionList?.[index]?.cp,
                                  ',0.00'
                                )
                              : rawMaterialData.cp[index]) ?? ''
                          }
                          onChange={({ target: { value } }) => {
                            setNutrition('cp', index, value);
                          }}
                        />
                      </td>
                      <td className="px-2">
                        <Form.Control
                          readOnly={!pathname.includes('setting')}
                          size="sm"
                          type="text"
                          value={
                            (!pathname.includes('setting')
                              ? formatNumber(
                                  inform.newNutritionList?.[index]?.cFat,
                                  ',0.00'
                                )
                              : rawMaterialData.cFat[index]) ?? ''
                          }
                          onChange={({ target: { value } }) => {
                            setNutrition('cFat', index, value);
                          }}
                        />
                      </td>
                      <td className="px-2">
                        <Form.Control
                          readOnly={!pathname.includes('setting')}
                          size="sm"
                          type="text"
                          value={
                            (!pathname.includes('setting')
                              ? formatNumber(
                                  inform.newNutritionList?.[index]?.cFib,
                                  ',0.00'
                                )
                              : rawMaterialData.cFib[index]) ?? ''
                          }
                          onChange={({ target: { value } }) => {
                            setNutrition('cFib', index, value);
                          }}
                        />
                      </td>
                      <td className="px-2">
                        <Form.Control
                          readOnly={!pathname.includes('setting')}
                          size="sm"
                          type="text"
                          value={
                            (!pathname.includes('setting')
                              ? formatNumber(
                                  inform.newNutritionList?.[index]?.ash,
                                  ',0.00'
                                )
                              : rawMaterialData.ash[index]) ?? ''
                          }
                          onChange={({ target: { value } }) => {
                            setNutrition('ash', index, value);
                          }}
                        />
                      </td>
                      <td className="px-2">
                        <Form.Control
                          readOnly={!pathname.includes('setting')}
                          size="sm"
                          type="text"
                          value={
                            (!pathname.includes('setting')
                              ? formatNumber(
                                  inform.newNutritionList?.[index]?.ca,
                                  ',0.00'
                                )
                              : rawMaterialData.ca[index]) ?? ''
                          }
                          onChange={({ target: { value } }) => {
                            setNutrition('ca', index, value);
                          }}
                        />
                      </td>
                      <td className="px-2">
                        <Form.Control
                          readOnly={!pathname.includes('setting')}
                          size="sm"
                          type="text"
                          value={
                            (!pathname.includes('setting')
                              ? formatNumber(
                                  inform.newNutritionList?.[index]?.p,
                                  ',0.00'
                                )
                              : rawMaterialData.p[index]) ?? ''
                          }
                          onChange={({ target: { value } }) => {
                            setNutrition('p', index, value);
                          }}
                        />
                      </td>
                      <td className="px-2">
                        <Form.Control
                          readOnly={!pathname.includes('setting')}
                          size="sm"
                          type="text"
                          value={
                            (!pathname.includes('setting')
                              ? formatNumber(
                                  inform.newNutritionList?.[index]?.starch,
                                  ',0.00'
                                )
                              : rawMaterialData.starch[index]) ?? ''
                          }
                          onChange={({ target: { value } }) => {
                            setNutrition('starch', index, value);
                          }}
                        />
                      </td>
                      <td className="w-25 px-2">
                        {/* View */}
                        <Form.Control
                          readOnly
                          size="sm"
                          type="text"
                          value={value}
                        />
                      </td>
                    </tr>
                  ))}
                </List>
              </Col>
            </Row>
            <Row>
              <Col sm={12}>
                <Form.Label>
                  {getText('file')}(
                  {format(getText('tooManyFiles'), { max: 3 })})
                </Form.Label>
                <Row>
                  {pathname.includes('setting') && (
                    <Col sm={8}>
                      <div
                        {...getRootProps({ className: 'dropzone-area py-4' })}
                      >
                        <input {...getInputProps()} />
                        <Flex
                          justifyContent="center"
                          alignItems="center"
                          className="h-100"
                        >
                          <img
                            src={cloudUpload}
                            alt="Drop your files here"
                            className="me-2"
                            width={25}
                          />
                          <p className="fs-0 mb-0 text-700">
                            Drop your files here
                          </p>
                        </Flex>
                      </div>
                    </Col>
                  )}
                  <Col sm={4}>
                    <div>
                      {inform.newFileList?.map?.(({ seq, originNm } = {}) => (
                        <Flex
                          key={`newFileList${seq}`}
                          alignItems="center"
                          className="py-3 border-bottom btn-reveal-trigger"
                        >
                          <Flex
                            justifyContent="between"
                            alignItems="center"
                            className="ms-3 flex-1"
                          >
                            <div>
                              <h6>{originNm}</h6>
                            </div>
                          </Flex>
                          <CardDropdown>
                            <div className="py-2">
                              {pathname.includes('setting') ? (
                                <Dropdown.Item
                                  className="text-danger"
                                  onClick={() => onDeleteFileClick(seq, true)}
                                >
                                  {getText('delete')}
                                </Dropdown.Item>
                              ) : (
                                <Dropdown.Item
                                  onClick={() => {
                                    requestFileDownload(seq);
                                  }}
                                >
                                  {getText('download')}
                                </Dropdown.Item>
                              )}
                            </div>
                          </CardDropdown>
                        </Flex>
                      ))}
                      {rawMaterialData.file?.map?.((value, index) => (
                        <Flex
                          key={`file${index}`}
                          alignItems="center"
                          className="py-3 border-bottom btn-reveal-trigger"
                        >
                          {value.type.includes('image') && (
                            <img
                              src={URL.createObjectURL(value)}
                              alt={value.name}
                              className="rounded"
                              style={{
                                width: '40px',
                                height: '40px'
                              }}
                            />
                          )}
                          <Flex
                            justifyContent="between"
                            alignItems="center"
                            className="ms-3 flex-1"
                          >
                            <div>
                              <h6>{value.name}</h6>
                              <Flex
                                alignItems="center"
                                className="position-relative"
                              >
                                <p className="mb-0 fs--1 text-400 line-height-1">
                                  <strong>{getSize(value.size)}</strong>
                                </p>
                              </Flex>
                            </div>
                          </Flex>
                          <CardDropdown>
                            <div className="py-2">
                              <Dropdown.Item
                                className="text-danger"
                                onClick={() => onDeleteFileClick(index)}
                              >
                                {getText('delete')}
                              </Dropdown.Item>
                            </div>
                          </CardDropdown>
                        </Flex>
                      ))}
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
            {!pathname.includes('setting') && (
              <>
                <Divider className="mt-4" />
                <Comment
                  datas={inform.newCommentList}
                  seq={Math.floor(seq)}
                  requestInform={requestInform}
                />
              </>
            )}
          </Fragment>
          <Fragment key="footerButton">
            {pathname.includes('setting') &&
              !seq &&
              adminYn === RETURN_YN.Y && (
                <Button
                  size="sm"
                  className="ms-auto"
                  onClick={() => setIsShowCreate(true)}
                >
                  {getText('createRawMaterial')}
                </Button>
              )}
            <Button
              size="sm"
              variant="secondary"
              className={classNames({
                'ms-2': pathname.includes('setting'),
                'ms-auto': !pathname.includes('setting')
              })}
              onClick={showList}
            >
              {getText(pathname.includes('setting') ? 'cancel' : 'list')}
            </Button>
            {pathname.includes('setting') ? (
              <Button
                disabled={isDisableSetting}
                size="sm"
                type="submit"
                className="ms-2"
              >
                {getText('ok')}
              </Button>
            ) : (
              <>
                {adminYn === RETURN_YN.Y && inform.sttus === 0 && (
                  <>
                    <Button
                      disabled={isDisableReject}
                      size="sm"
                      variant="danger"
                      className="ms-2"
                      onClick={onRejectClick}
                    >
                      {getText('reject')}
                    </Button>
                    <Button
                      size="sm"
                      className="ms-2"
                      onClick={() => setIsShowCreate(true)}
                    >
                      {getText('createRawMaterial')}
                    </Button>
                  </>
                )}
                {(adminYn === RETURN_YN.Y || inform.userFlag === RETURN_YN.Y) &&
                  inform.sttus === 0 && (
                    <>
                      <Button
                        size="sm"
                        variant="success"
                        className="ms-2"
                        onClick={() => {
                          history.push(
                            format(ROUTER_PATH.board.rawMaterial.setting, {
                              seq
                            }),
                            state
                          );
                        }}
                      >
                        {getText('update')}
                      </Button>
                      <Button
                        disabled={isDisableDelete}
                        size="sm"
                        variant="danger"
                        className="ms-2"
                        onClick={onDeleteClick}
                      >
                        {getText('delete')}
                      </Button>
                    </>
                  )}
              </>
            )}
          </Fragment>
        </Content>
      </Form>
      <Create
        show={isShowCreate}
        inform={rawMaterialData}
        isSetting={pathname.includes('setting')}
        seq={seq}
        onHide={() => setIsShowCreate(false)}
        onSetting={create}
      />
    </>
  );
};

export default Inform;
